import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { isEmpty, isEqual } from 'lodash';
import { useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';

import { useNotifications } from '@/hooks/use-notifications';
import { DEFAULT_PAGINATION, DURATION, SHIPMENT_GROUPING } from '@/lib/constants';
import { CreateTrackingRequestBody, ShipmentGrouping } from '@/lib/models/shipment/types';
import { ShipmentsApiResponse, TrackingRequestApiResponse } from '@/types/api-types';
import { request } from '@/utils/api/request';
import {
  getSharedItemsShipmentsUrl,
  getShipmentFiltersUrl,
  getShipmentsUrl,
  getShipmentTagsUrl,
  getTagsUrl,
  getTrackingRequestsUrl,
} from '@/utils/helpers/api-urls';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';

const ERROR_MESSAGE = 'Error fetching shipments';

interface UseShipmentsProps {
  pagination?: PaginationState;
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  grouping?: ShipmentGrouping;
  isEnabled?: boolean;
  sharedItemId?: string;
}

export const useShipments = ({
  pagination = DEFAULT_PAGINATION,
  sorting,
  columnFilters,
  isEnabled = true,
  grouping = SHIPMENT_GROUPING.NONE,
  sharedItemId,
}: UseShipmentsProps = {}) => {
  const { addNotification } = useNotifications();

  const [hasSearched, setHasSearched] = useState(false);

  const filters: Record<string, any> | undefined = (() => {
    let query = {};
    if (pagination) {
      query = {
        ...query,
        ...pagination,
      };
    }

    if (sorting?.[0]) {
      query = {
        ...query,
        [QUERY_PARAM.SORTING]: JSON.stringify(sorting[0]),
      };
    }
    if (!isEmpty(columnFilters)) {
      query = {
        ...query,
        [QUERY_PARAM.FILTERS]: JSON.stringify(columnFilters),
      };
    }

    if (sharedItemId) {
      query = {
        ...query,
        [QUERY_PARAM.SHARED_ITEM]: sharedItemId,
      };
    }

    query = {
      ...query,
      [QUERY_PARAM.GROUPING]: grouping,
    };

    return isEmpty(query) ? undefined : query;
  })();

  const getUrl = () => {
    if (!isEnabled) return null;

    const baseUrl = sharedItemId ? getSharedItemsShipmentsUrl(sharedItemId) : getShipmentsUrl();
    return filters ? `${baseUrl}?${new URLSearchParams(filters)}` : baseUrl;
  };

  const {
    isLoading,
    data,
    error,
    mutate: mutateShipmentsListing,
  } = useSWR<ShipmentsApiResponse>(getUrl(), request, {
    keepPreviousData: true,
    errorRetryCount: 2,
    refreshInterval: DURATION.IN_MILLISECONDS.TEN_MINS,
    revalidateOnFocus: false,
    onSuccess: () => {
      setHasSearched(true);
    },
    onError: () => {
      setHasSearched(true);
    },
  });

  const createShipment = async (shipment: CreateTrackingRequestBody) => {
    return request<TrackingRequestApiResponse>(getTrackingRequestsUrl(), { body: shipment }, 'POST');
  };

  const setShipmentTags = async ({ labels, shipmentId }: { labels: string[]; shipmentId: string }) => {
    try {
      await request(getShipmentTagsUrl(shipmentId), { body: { labels: labels.filter(Boolean), shipmentId } }, 'POST');
      globalMutate(getTagsUrl());
      globalMutate(getShipmentFiltersUrl());
      mutateShipmentsListing();
    } catch (e) {
      addNotification({
        heading: 'Error updating tags',
        description: 'There was an error adding the tags. Please try again.',
      });
    }
  };

  const errorMessage = error ? ERROR_MESSAGE : undefined;
  return {
    isLoading,
    shipments: data?.shipments,
    error: errorMessage,
    createShipment,
    hasSearched,
    count: data?.count || 0,
    countByStatus: data?.countByStatus || [],
    hasAppliedFilters: !isEqual(DEFAULT_PAGINATION, filters),
    setShipmentTags,
  };
};
